export const basicsArticles = [
  {
    slug: 'introduction',
    title: 'Introduction',
    summary: 'Learn how AdMobilize collects data from physical locations, organizes it, and helps you understand the Sites, Assets, or Devices behind a trend.',
    category: 'basics',
    sources: [{ file: 'login-page.pdf' }, { file: 'summary.pdf' }, { file: 'main-filters.pdf' }],
    sections: [
      {
        id: 'about-admobilize',
        title: 'What AdMobilize helps you understand',
        paragraphs: [
          'Most teams come to AdMobilize with a practical question about a physical location. They may want to know when traffic peaks, how many people or vehicles passed by, whether an audience noticed a screen, or how one location compares with another. AdMobilize collects the measurements behind those questions and brings them into a Dashboard where your team can explore trends, investigate changes, and share what it learns.',
          'The source of those measurements depends on your deployment. Camera-based Devices can measure audience and traffic activity, while Mobile Data provides location-level insights without requiring a camera or Device. Some Sites use one source; others combine both. When cameras are involved, AdMobilize processes the video input at the edge and keeps the anonymous, aggregate measurements needed for analytics. It does not retain people’s identities or a stored video feed.',
          'Once the data reaches the Dashboard, you can begin with a broad view in Summary and work toward the details. The Charts page shows how a metric changed over time, while Insights helps you compare Sites or Devices. When you need to share the result, export a chart or create a one-time or recurring report. Integrations can add business context too: a CMS integration such as Broadsign can place proof-of-play data alongside AdMobilize measurements.'
        ],
        paragraphLink: { label: 'Broadsign', href: 'https://broadsign.com/' }
      },
      {
        id: 'dashboard-structure',
        title: 'How your data is organized',
        paragraphs: [
          'You do not need to learn the whole data model before using the Dashboard. Start with a Site Group, choose a Site, and follow the structure that matches your deployment. Mobile Data belongs directly to a Site, while camera measurements flow through Devices connected to Assets.',
          'For example, a media owner can use an Asset to represent a screen, connect the Devices that measure the area around it, and map that Asset to a CMS player. A retailer or venue can use Mobile Data at the Site level without installing additional hardware. Both arrangements can live in the same Site Group and use the same Dashboard tools.'
        ],
        tables: [
          {
            caption: 'Basic Dashboard concepts',
            headers: ['Concept', 'What it means'],
            rows: [
              ['Dashboard', 'Where you review measurements, manage your deployment, and create exports or reports.'],
              ['Site Group', 'The workspace that controls which Sites, Users, settings, and data your team can access.'],
              ['Site', 'A physical location or installation. A Site can receive Mobile Data directly and contain one or more Assets.'],
              ['Asset', 'A screen or other media item at a Site. It can be mapped to a CMS player and connected to one or more Devices.'],
              ['Device', 'Hardware running an AdMobilize solution. It receives camera input and contributes Audience or Traffic measurements to an Asset.'],
              ['Mobile Data', 'Location-level audience data attached directly to a Site. No camera or Device is required.'],
              ['User', 'Someone with access to one or more Site Groups. Their role controls what they can view or manage.']
            ]
          }
        ]
      },
      {
        id: 'first-minutes',
        title: 'Your first few minutes in the Dashboard',
        paragraphs: [
          'Sign in with your AdMobilize account. If you cannot remember your password, select Forgot Password and follow the recovery link sent to your account email.',
          'After sign-in, Summary gives you the quickest read on what happened. Its KPIs and main chart reflect the Site Group, solution, data sources, and period you selected. Each change indicator compares your chosen period with the immediately preceding period of the same length. If you select 15 days, the Dashboard compares them with the 15 days immediately before.',
          'Summary is meant to point you in a direction, not answer every question. When a number moves, open Charts to see when it changed or use Insights to compare the Sites or Devices behind the result.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/dashboard-login-high-resolution.jpg',
            frame: { width: 1416, height: 1068, sourceWidth: 1572 },
            alt: 'AdMobilize Dashboard sign-in page with email and password fields',
            caption: 'Use your AdMobilize account credentials to sign in.'
          }
        ],
        tables: [
          {
            caption: 'Examples of what you can review by measurement source',
            headers: ['Source', 'Examples'],
            rows: [
              ['Audience', 'Views, impressions, engagement, and supported audience classifications'],
              ['Traffic', 'People and vehicle counts, speed, direction, zones, and supported vehicle classifications'],
              ['Mobile Data', 'Impressions, reach, frequency, and other location-level measurements']
            ]
          }
        ]
      },
      {
        id: 'narrow-the-view',
        title: 'Narrow the view to match your question',
        paragraphs: [
          'The first result is often a starting point. Use the controls above the Dashboard to narrow it to the location, data source, and time period that match the question you are asking. Choose the Site Group and solution first, then adjust the dates and grouping.',
          'For camera-based solutions, choose the Devices you want to include. Select one Device when you are investigating a specific installation, or combine several when you want a broader view. Mobile Data views use Sites instead. Review the selection before updating analytics or creating a report.',
          'The Device list also shows status information. Request a preview only when you need to check the camera angle or field of view, limit preview access to authorized Users, and follow your organization’s privacy and retention policies.',
          'The Dashboard uses the timezone saved in the selected Site Group for charts and generated reports. Confirm it before comparing locations in different regions or sharing a report with someone who expects a different timezone.'
        ],
        bullets: [
          'Quick ranges include Today, Yesterday, Last 7 Days, Last Month, and This Month.',
          'Use the calendar for a custom start and end date.',
          'Enable Between to limit results to a time of day.',
          'Select weekdays to include only those days in the result.',
          'Group data by Hour, Day, Week, Week (Starting on Monday), Month, or Year.'
        ],
        figures: [
          {
            src: 'assets/documentation/dashboard-date-filter.png',
            alt: 'Dashboard date-range selector showing a calendar and quick ranges',
            caption: 'Use the date controls to define the reporting period.'
          }
        ]
      },
      {
        id: 'follow-an-insight',
        title: 'From a change to an explanation',
        paragraphs: [
          'Imagine Summary shows more activity than the previous week. Rather than treating the percentage as the answer, open Charts and group the data by day or hour to find when the increase began. Then use Insights to see which Sites or Devices contributed most.',
          'If the change matters to colleagues or customers, export the chart or create a Report using the same scope. Include the Site Group, time period, timezone, and selected sources so readers understand exactly what the numbers cover.'
        ],
        steps: [
          'Open Summary and choose the Site Group, solution, and period you want to review.',
          'Read the headline KPIs and the trend below them. Check the selected period before interpreting the change indicators.',
          'Continue to Analytics → Charts for detailed widgets or Analytics → Insights to compare sources.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/summary-audience-overview.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Audience Summary with impressions, confirmed views, engagement, and a time-series chart',
            caption: 'Example: start with Audience totals in Summary, then investigate the same scope in Charts.'
          }
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Summary', to: '/summary' },
      { label: 'Open Profile', to: '/profile' }
    ],
    related: ['analytics-and-insights'],
    nextArticle: {
      slug: 'navigating-the-dashboard',
      description: 'Next, take a short tour of the Dashboard and learn where to begin as your questions become more specific.'
    },
    legacy: false
  },
  {
    slug: 'profile-and-site-groups',
    title: 'Profile and Site Groups',
    summary: 'Learn where your personal settings end, where Site Group settings begin, and how those choices affect the people and data in your workspace.',
    category: 'basics',
    sources: [{ file: 'profile.pdf' }, { file: 'projects.pdf' }],
    sections: [
      {
        id: 'profile',
        title: 'Review profile details',
        paragraphs: [
          'Profile is the starting point for settings connected to your own account. Here you can confirm your account name, User ID, and email address, change your password, or follow the links to support and documentation. The same area also opens the settings for each Site Group you can access, including preferences that affect how analytics are presented.',
          'You can also create API keys from Profile. If you are setting up an integration, Managing API keys shows how to create one and what to do when it needs replacing.'
        ],
        paragraphLinks: [{ label: 'Managing API keys', slug: 'managing-api-keys', section: 'create-api-key' }],
        bullets: [
          'Change your password from the personal-information area.',
          'Mask Data makes the mask widget available for compatible Audience deployments.',
          'Select a Site Group before reviewing its preferences or provisioning information.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/profile-personal-information-and-api-keys.jpg',
            frame: { width: 2116, height: 556, sourceWidth: 2362 },
            alt: 'Profile personal information and API Keys card',
            caption: 'Open Profile to review your own account or create an integration key.'
          }
        ]
      },
      {
        id: 'site-group-details',
        title: 'Review Site Group details',
        paragraphs: [
          'A Site Group is the shared workspace around a deployment, so its details reach beyond one person’s profile. The Site Group drawer contains Settings, Sites, Permissions, and Preferences. Use these sections to review its identity, locations, access, and analytics settings.',
          'Some of the values on this page can grant access to a deployment. Treat Site Group and Device provisioning tokens as secrets, and never paste them into support tickets, shared documents, or source control.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/management-site-groups-directory.jpg',
            frame: { width: 2608, height: 1247, sourceWidth: 2917 },
            alt: 'Site Group directory with names and device totals',
            caption: 'Open a Site Group from Management → Site Groups to review its settings and access.'
          }
        ]
      },
      {
        id: 'invite-users',
        title: 'Invite Users to a Site Group',
        paragraphs: [
          'Invite a colleague from the Permissions section of the Site Group where they will work. The invitation email provides a link to join that Site Group and, when needed, create an account. Users, Roles, and Access explains how to choose a role.'
        ],
        paragraphLinks: [{
          label: 'Users, Roles, and Access',
          slug: 'users-roles-and-access',
          section: 'role-differences'
        }],
        steps: [
          'Open Management → Site Groups and open the required Site Group.',
          'Expand Permissions and select Invite User.',
          'Enter the recipient’s email address and choose Dashboard User or Data Viewer.',
          'Review the email address and role, then select Save to send the invitation.'
        ],
        tables: [
          {
            caption: 'Site Group roles shown in the Dashboard',
            headers: ['Role', 'Access'],
            rows: [
              [
                'Dashboard User',
                'Can use Profile, Summary, Analytics, and Reports and can modify available Device, Site Group, and report settings.'
              ],
              ['Data Viewer', 'Can use Profile, Summary, Analytics, and Reports but cannot modify settings.']
            ]
          }
        ],
        figures: [
          {
            src: 'assets/documentation/examples/site-groups-invite-user-and-select-role.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Site Group invitation form with email address and role selector',
            caption: 'Choose the role for this Site Group before submitting the invitation.'
          }
        ]
      },
      {
        id: 'transfer-devices',
        title: 'Transfer Devices between Site Groups',
        paragraphs: [
          'Organizations with more than one Site Group may occasionally need to move a Device to a different workspace. An authorized User can complete the transfer from the source Site Group. Afterward, select the destination in the Site Group filter before looking for the Device or its data.'
        ],
        steps: [
          'Open the source Site Group and select Devices.',
          'Select the Devices to move and choose the transfer action.',
          'Select the destination Site Group and confirm the transfer.'
        ]
      },
      {
        id: 'site-group-preferences',
        title: 'Set Site Group preferences',
        paragraphs: [
          'Site Group preferences give a team a consistent interpretation of its analytics. They control units, time limits, the threshold used to count a view, the timezone applied to charts and reports, and the multipliers behind estimated vehicle impressions. The Dashboard begins with default time limits and a default view threshold, but an authorized User can adjust them for the Site Group.'
        ],
        tables: [
          {
            caption: 'Analytics preferences',
            headers: ['Preference', 'Purpose'],
            rows: [
              ['Speed Unit', 'Displays speed in kilometers/hour, miles/hour, or meters/second.'],
              [
                'Max Session Time',
                'Excludes Audience events whose session time reaches the configured upper limit; the initial value is 120 seconds.'
              ],
              [
                'Max Attention Time',
                'Excludes Audience events whose attention time reaches the configured upper limit; the initial value is 60 seconds.'
              ],
              [
                'View Threshold',
                'Counts an eligible impression as a view only when attention time is greater than the threshold; the initial value is 0.5 seconds.'
              ],
              ['Timezone', 'Converts charts and reports to the Site Group timezone.'],
              ['Vehicle Multiplier', 'Sets editable per-type factors used by estimated-impression metrics.']
            ]
          }
        ],
        steps: [
          'Open the Site Group and expand Preferences.',
          'Review the timezone, speed unit, attention and session limits, and view threshold.',
          'Change only the preferences needed for the deployment, then use the save control to apply them.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/site-groups-preferences-timezone-units-and-thresholds.jpg',
            frame: { width: 2608, height: 1247, sourceWidth: 2917 },
            alt: 'Site Group Preferences with timezone, speed units, and view thresholds',
            caption: 'Preferences apply to analytics for this Site Group; use values appropriate to your deployment.'
          }
        ]
      }
    ],
    dashboardLinks: [{ label: 'Open Profile', to: '/profile' }],
    related: ['introduction', 'managing-api-keys', 'workspace-management'],
    nextArticle: {
      slug: 'managing-api-keys',
      description: 'Next, learn how to create an API key, keep it safe, and replace it when needed.'
    },
    legacy: false
  },
  {
    slug: 'managing-api-keys',
    title: 'Managing API keys',
    summary: 'Create, review, and revoke keys for integrations that call the Developer API.',
    category: 'basics',
    sources: [],
    sections: [
      {
        id: 'api-key-access',
        title: 'Know what a key can access',
        paragraphs: [
          'An API key lets your integration call the Developer API without signing in with your account credentials. It has the same access to Site Groups and data as the account that created it. Create the key from an account with the access your integration needs.',
          'API keys authenticate Developer API requests only. They cannot sign someone in to the Dashboard or manage other keys. Make a separate key for each integration so you can replace one without interrupting the others. When the key is ready, Getting started shows how to send it with a request.'
        ],
        paragraphLinks: [{ label: 'Getting started', slug: 'public-api-getting-started', section: 'request-conventions' }]
      },
      {
        id: 'create-api-key',
        title: 'Create and save a key',
        paragraphs: [
          'The Dashboard shows the full key only once. Have a secure place ready to save it before you begin.'
        ],
        steps: [
          'Open Profile and find the API Keys card. Select Create API Key.',
          'Enter a Name that identifies the integration. If the key should expire automatically, set a future date and time in Expires at (optional).',
          'Select Generate API Key. Copy the full key from the dialog and save it in your secret manager.',
          'After you have saved the key, select I\'ve Saved It to close the dialog.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/profile-create-api-key-name-and-expiry.jpg',
            displayWidth: 500,
            alt: 'Create API Key dialog with Name and optional expiry fields',
            caption: 'Give the key an integration-specific name and optional expiry before generating it.'
          }
        ]
      },
      {
        id: 'review-api-keys',
        title: 'Review your keys',
        paragraphs: [
          'The API Keys card lists each key’s name, prefix, creation date, expiry, and status. Use the prefix to identify a key without exposing its secret. Select Load more if you have more keys than the page shows.',
          'An expired key can no longer authenticate requests. The list does not reveal the full key again, so create a replacement if you no longer have the secret.'
        ]
      },
      {
        id: 'replace-or-revoke-api-key',
        title: 'Replace or revoke a key',
        paragraphs: [
          'To rotate a key, create a replacement and update the integration to use it. Once requests work with the new key, select Revoke beside the old one and confirm with Revoke Key. Revocation takes effect immediately.',
          'If you think a key has been exposed, revoke it right away and create a replacement. Keep keys out of browser code, URLs, logs, and source control.'
        ]
      }
    ],
    dashboardLinks: [{ label: 'Open Profile', to: '/profile' }],
    related: ['profile-and-site-groups', 'public-api-getting-started'],
    nextArticle: {
      slug: 'users-roles-and-access',
      description: 'Next, learn how Site Group membership and roles determine what each User can see and manage.'
    },
    legacy: false
  },
  {
    slug: 'navigating-the-dashboard',
    title: 'Navigating the Dashboard',
    summary: 'Learn where to begin, how the main Dashboard areas fit together, and where to go as your question becomes more specific.',
    category: 'basics',
    sources: [{ file: 'summary.pdf' }, { file: 'management.pdf' }, { file: 'profile.pdf' }],
    sections: [
      {
        id: 'start-with-a-question',
        title: 'Start with the question you want to answer',
        paragraphs: [
          'The Dashboard is easier to navigate when you begin with a question instead of a page. If you want a quick view of recent activity, start in Summary. If a number has changed and you want to know when or where, continue to Charts or Insights. When the result is ready to share, move to Reports or export the chart you are reviewing.',
          'The options you see can vary with your account, role, and enabled capabilities. A page that is available to one team may not be part of another team’s workflow, so use the navigation shown in your account as the source of truth.'
        ]
      },
      {
        id: 'analytics-areas',
        title: 'Move from the overview to the detail',
        tables: [
          {
            caption: 'Main analytics areas',
            headers: ['Area', 'When to use it'],
            rows: [
              ['Summary', 'Get a quick view of key metrics and change for the selected scope and period.'],
              ['Charts', 'Explore individual metrics, change the time grouping, and export chart data.'],
              ['Insights', 'Compare the Sites or Devices contributing to a result.'],
              ['Discovery', 'Explore Mobile Data geographically and compare the available Site-level metrics.']
            ]
          }
        ],
        paragraphs: [
          'These areas are different views of the same underlying scope. Before comparing their results, confirm that you are using the same Site Group, Dashboard view, Sites or Devices, date range, interval, and timezone.'
        ]
      },
      {
        id: 'management-areas',
        title: 'Manage the workspace around the data',
        paragraphs: [
          'The management pages describe the real-world deployment behind your analytics. Sites represent locations, Assets represent media or other measurable items at those locations, and Devices represent connected hardware. Reports, Integrations, and Site Groups help you share results, add outside context, and control how the workspace is organized.',
          'You do not need to visit every management page before using analytics. Open one when you need to understand a measurement’s source, review the health of a Device, update an association, or confirm who can access a Site Group.'
        ]
      },
      {
        id: 'keep-your-place',
        title: 'Keep your place as you move around',
        paragraphs: [
          'A change of page does not make two results comparable by itself. Read the active Site Group and filters whenever you move between Summary, Charts, Insights, and Reports. The Site Group also supplies the timezone used to present analytics, which matters when locations operate in different regions.',
          'If a page does not show what you expected, first check the current Site Group and selected Dashboard view. Those two choices determine much of the navigation, data, and filtering that follows.'
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Summary', to: '/summary' },
      { label: 'Open Charts', to: '/charts' },
      { label: 'Open Sites', to: '/sites' }
    ],
    related: ['introduction', 'analytics-and-insights', 'workspace-management'],
    nextArticle: {
      slug: 'sites-assets-and-devices',
      description: 'Next, see how the Dashboard connects your workspace with physical locations, media, and measurement Devices.'
    },
    legacy: false
  },
  {
    slug: 'sites-assets-and-devices',
    title: 'Understanding Sites, Assets, and Devices',
    summary: 'See how the Dashboard represents your organization, physical locations, media, and connected hardware.',
    category: 'basics',
    sources: [{ file: 'management.pdf' }, { file: 'projects.pdf' }],
    sections: [
      {
        id: 'from-workspace-to-measurement',
        title: 'Follow the structure from workspace to measurement',
        paragraphs: [
          'AdMobilize organizes a deployment from broad to specific. A Site Group defines the workspace and access boundary. Inside it, Sites represent physical locations, Assets represent the items being measured at those locations, and Devices provide camera-based measurements for those Assets. Mobile Data follows a shorter path because it belongs directly to a Site and does not require an Asset or Device.',
          'This structure keeps analytics connected to the real world. When a result changes, you can move from the Site Group total to a particular Site, then identify the Asset or Device that contributed to it.'
        ],
        tables: [
          {
            caption: 'Dashboard organization',
            headers: ['Level', 'What it represents', 'Can contain or connect to'],
            rows: [
              ['Site Group', 'A workspace and access boundary for an organization or team.', 'Sites, Users, preferences, and available data.'],
              ['Site', 'A physical location or installation.', 'Assets and Site-level data sources such as Mobile Data.'],
              ['Asset', 'A screen or another physical media item at a Site.', 'CMS mappings and one or more Devices.'],
              ['Device', 'Connected hardware that receives camera input and runs an AdMobilize solution.', 'Audience or Traffic measurements associated with an Asset.']
            ]
          }
        ]
      },
      {
        id: 'sites-and-assets',
        title: 'Use Sites and Assets to match the real deployment',
        paragraphs: [
          'Create the structure that helps your team recognize the deployment. A Site might be a store, venue, transit station, roadside location, or building. Within that Site, an Asset can identify the particular screen or media placement connected to a measurement or CMS player.',
          'Clear names matter once the workspace grows. Names that include the location and placement make filters, exports, and reports easier to understand than internal codes that only the installation team recognizes.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/management-sites-directory.jpg',
            frame: { width: 2120, height: 1008, sourceWidth: 2362 },
            alt: 'Site directory with location names and asset counts',
            caption: 'Recognizable Site names help users choose the right location in filters and reports.'
          }
        ]
      },
      {
        id: 'devices-and-data',
        title: 'Connect Devices to the Assets they measure',
        paragraphs: [
          'A Device is the connected hardware running an AdMobilize camera-based solution. Its camera input is processed at the edge, and the resulting anonymous, aggregate measurements are sent to the platform. Associating the Device with the correct Asset gives those measurements useful location and media context.',
          'A Site can contain several Assets, and an Asset can have more than one Device when the deployment requires it. Select only the Devices relevant to your question when you analyze a particular entrance, screen, direction, or coverage area.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/sites-edit-asset-two-attached-devices.jpg',
            frame: { width: 2626, height: 1245, sourceWidth: 2917 },
            alt: 'Asset editor with an Audience device and a Traffic device attached',
            caption: 'One Asset can combine measurements from two Devices, such as an Audience camera and a Traffic camera.'
          }
        ]
      },
      {
        id: 'example-structure',
        title: 'See the structure in a practical example',
        paragraphs: [
          'In this example workspace, Mission has two Assets. Both use the Front Floor Traffic Camera, while the Entrance Asset also has an Audience camera. A shared Device measures the same scene for both Assets; its attachment does not represent two independent cameras.',
          'Market Street is a Mobile-only Site. Its Assets list is empty because Mobile Data is associated with the Site itself.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/sites-mission-two-assets-sharing-a-traffic-device.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Mission Site showing two Assets connected to the same Traffic device',
            caption: 'Two Assets can share a Device when both placements use measurements from the same scene.'
          },
          {
            src: 'assets/documentation/examples/sites-mobile-only-site-without-assets.jpg',
            frame: { width: 2626, height: 1245, sourceWidth: 2917 },
            alt: 'Mobile-only Market Street Site with an empty Assets list',
            caption: 'A Mobile-only Site can have no Assets or Devices.'
          }
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Site Groups', to: '/sitegroups' },
      { label: 'Open Sites', to: '/sites' },
      { label: 'Open Devices', to: '/devices' }
    ],
    related: ['profile-and-site-groups', 'workspace-management', 'mobile-data-in-the-dashboard'],
    nextArticle: {
      slug: 'solutions-and-data-sources',
      description: 'Next, learn how solutions and data sources determine which measurements and Dashboard views are available.'
    },
    legacy: false
  },
  {
    slug: 'filters-and-date-ranges',
    title: 'Working with Filters and Date Ranges',
    summary: 'Define the locations, data sources, period, and time grouping behind an analytics result.',
    category: 'basics',
    sources: [{ file: 'main-filters.pdf' }, { file: 'summary.pdf' }, { file: 'charts-and-insights.pdf' }],
    sections: [
      {
        id: 'define-the-scope',
        title: 'Define what the result should include',
        paragraphs: [
          'Filters turn a broad Dashboard view into a specific question. Start with the Site Group and Dashboard view, then choose the Sites or Devices that belong in the result. Apply the date and time controls last, once you know which part of the deployment you are studying.',
          'Before sharing or comparing a number, read the active filters as part of the result. A total for every Site in a month answers a different question from a total for one Device during business hours, even when both use the same metric.'
        ],
        steps: [
          'Select the Site Group in the top bar.',
          'Open the solution selector and choose the view that answers your question.',
          'Check the source and date filters before reading the result.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/filters-choose-solution.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Dashboard solution selector with Audience, Traffic, and Mobile views',
            caption: 'Choose the solution before narrowing the sources and reporting period.'
          }
        ]
      },
      {
        id: 'choose-sources',
        title: 'Choose the Sites or Devices that contribute data',
        paragraphs: [
          'Camera-based views use Devices as the measurement source. Select one Device to investigate a particular installation or select several to understand a larger area. Views based on Mobile Data use Sites instead, because that data source is connected at the Site level.',
          'Changing a Site selection can also change the Assets and Devices available farther down the filter. If an expected Device is missing, confirm its Site and Asset associations before assuming that its data is unavailable.'
        ],
        steps: [
          'Open the source selector above the charts.',
          'Select the Sites, Assets, or Devices relevant to the view. Expand a Site to inspect its available sources.',
          'Review the selected tags before continuing.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/filters-sites-assets-devices.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Dashboard source selector showing Sites, Assets, and Devices',
            caption: 'Use the location hierarchy to include the sources relevant to the analysis.'
          }
        ]
      },
      {
        id: 'choose-time',
        title: 'Choose a period and an interval',
        paragraphs: [
          'The date range determines which events belong in the result, while the interval determines how those events are grouped. A 30-day range grouped by day reveals daily movement; the same range grouped by month compresses it into a much broader summary.',
          'Use quick ranges for common questions and the calendar for a custom start and end date. When operating hours matter, use the time-of-day and weekday controls to exclude periods that are outside the question you are investigating.'
        ],
        tables: [
          {
            caption: 'Common time controls',
            headers: ['Control', 'What it changes'],
            rows: [
              ['Date range', 'The first and last dates included in the result.'],
              ['Between', 'The time of day included on each selected date.'],
              ['Weekdays', 'The days of the week included in the result.'],
              ['Interval', 'How the selected data is grouped, such as Hour, Day, Week, Month, or Year.'],
              ['Timezone', 'How timestamps are interpreted and displayed.']
            ]
          }
        ],
        figures: [
          {
            src: 'assets/documentation/examples/filters-time-grouping-options.jpg',
            frame: { width: 2120, height: 1008, sourceWidth: 2362 },
            alt: 'Time grouping menu with Hour, Day, Week, Month, and Year options',
            caption: 'Use a finer interval to investigate peaks and a broader interval to review longer trends.'
          }
        ]
      },
      {
        id: 'compare-periods',
        title: 'Make comparisons on equal terms',
        paragraphs: [
          'Summary and Insights compare the selected period with the immediately preceding period of the same length. A seven-day selection is compared with the seven days before it. Check for holidays, closures, campaigns, or operating-hour changes before treating that percentage as a like-for-like comparison.',
          'The Dashboard uses the timezone saved in the Site Group. Confirm it when you compare locations in different regions, especially around midnight or a daylight-saving change.'
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Summary', to: '/summary' },
      { label: 'Open Charts', to: '/charts' },
      { label: 'Open Insights', to: '/insights' }
    ],
    related: ['analytics-and-insights', 'why-data-may-be-missing-or-delayed'],
    nextArticle: {
      slug: 'analytics-and-insights',
      description: 'Next, use those scope and time choices to investigate results in Charts and Insights.'
    },
    legacy: false
  },
  {
    slug: 'solutions-and-data-sources',
    title: 'Understanding Solutions and Data Sources',
    summary: 'Understand the difference between what the Dashboard measures, where the data comes from, and which views are available.',
    category: 'basics',
    sources: [
      { file: 'summary.pdf' },
      { file: 'audience-analytics---dashboard-charts.pdf' },
      { file: 'vehicle-detection---dashboard-charts.pdf' }
    ],
    sections: [
      {
        id: 'two-related-ideas',
        title: 'Separate the question from the source',
        paragraphs: [
          'The two vision solutions are Audience and Traffic. Audience measures face-based attention and supported audience attributes. Traffic covers people and vehicle movement, counts, speed, zones, and supported vehicle classifications. A data source describes where those measurements come from. Keeping solutions and data sources separate helps explain why two Dashboard views can show different metrics even when they cover the same Site Group and dates.',
          'The Dashboard only offers views that are compatible with the data available to your Site Group. If a solution or view is not listed, the required data source or account configuration may not be enabled.'
        ]
      },
      {
        id: 'available-sources',
        title: 'Recognize the main data sources',
        tables: [
          {
            caption: 'Data sources in the Dashboard',
            headers: ['Data source', 'How it is connected', 'Examples of what it can support'],
            rows: [
              ['Audience', 'Camera-based Devices associated with Assets.', 'Impressions, views, engagement, and supported audience classifications.'],
              ['Traffic', 'Camera-based Devices associated with Assets.', 'People or vehicle counts, direction, speed, zones, and supported classifications.'],
              ['Mobile Data', 'Connected directly to Sites without requiring Devices.', 'Impressions, reach, frequency, signal volume, match rate, and geographic exploration when available.']
            ]
          }
        ],
        paragraphs: [
          'The exact metrics depend on the selected Dashboard view, the deployed solution, and the fields available in your data. A label appearing elsewhere in the product does not guarantee that every Site or Device can provide it.'
        ]
      },
      {
        id: 'combined-context',
        title: 'Use combined views when they are available',
        paragraphs: [
          'Some account configurations can place more than one compatible source in the same Dashboard view. For example, Mobile Data can add Site-level context to supported Audience workflows. Integrations can add another layer, such as proof-of-play information from a CMS.',
          'A combined view does not make every metric interchangeable. Check the information shown with each chart and keep the source in mind when you explain what the result represents.'
        ]
      },
      {
        id: 'choose-a-view',
        title: 'Choose the view that matches the decision',
        paragraphs: [
          'Use an Audience view when the question concerns attention or exposure, a Traffic view when it concerns movement or vehicle activity, and a Mobile Data view when you need location-level reach and frequency context. Begin with the business question, then choose the view designed to answer it.'
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Summary', to: '/summary' },
      { label: 'Open Charts', to: '/charts' },
      { label: 'Open Integrations', to: '/integrations' }
    ],
    related: ['audience-metrics', 'traffic-metrics', 'mobile-data-in-the-dashboard'],
    nextArticle: {
      slug: 'filters-and-date-ranges',
      description: 'Next, define the locations, sources, dates, and intervals behind an analytics result.'
    },
    legacy: false
  },
  {
    slug: 'users-roles-and-access',
    title: 'Users, Roles, and Access',
    summary: 'Understand how Site Group membership and roles determine what each User can see and manage.',
    category: 'basics',
    sources: [{ file: 'profile.pdf' }, { file: 'projects.pdf' }],
    sections: [
      {
        id: 'access-follows-site-groups',
        title: 'Access follows Site Group membership',
        paragraphs: [
          'A User sees the data and management options available through their Site Group membership. Someone who belongs to several Site Groups can switch between them, while someone assigned to only one works inside that scope. This is why two colleagues can sign in to the same Dashboard and see different Sites, Devices, or features.',
          'When a User cannot find an expected location or Device, first confirm that they are in the correct Site Group. Adding access to a different Site Group is an administrative decision; it is not something a filter can solve.'
        ]
      },
      {
        id: 'role-differences',
        title: 'Choose a role that matches the work',
        paragraphs: [
          'Roles separate people who only need to review analytics from people who also manage settings. Give each User the access required for their work and avoid assigning broader permissions only to make an occasional task more convenient.'
        ],
        tables: [
          {
            caption: 'Site Group roles shown in the Dashboard',
            headers: ['Role', 'Typical access'],
            rows: [
              ['Dashboard User', 'Can use analytics and reports and can modify the settings available to the role.'],
              ['Data Viewer', 'Can review analytics and reports but cannot modify settings.']
            ]
          }
        ]
      },
      {
        id: 'invite-a-user',
        title: 'Invite someone to the right Site Group',
        paragraphs: [
          'Send the invitation from the Permissions section of the Site Group the person needs. Confirm the email address, role, and Site Group before submitting; these choices determine the recipient’s access.'
        ],
        steps: [
          'Open Management → Site Groups and open the required Site Group.',
          'Expand Permissions and select Invite User.',
          'Enter the recipient’s email address and choose Dashboard User or Data Viewer.',
          'Review the email address and role, then select Save to send the invitation.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/site-groups-user-permissions.jpg',
            frame: { width: 2608, height: 1247, sourceWidth: 2917 },
            alt: 'Site Group Permissions section with Invite User action',
            caption: 'Review membership and invitations in the Site Group’s Permissions section.'
          }
        ]
      },
      {
        id: 'review-access',
        title: 'Review access as responsibilities change',
        paragraphs: [
          'Review membership when someone changes teams, no longer supports a location, or no longer needs administrative access. Remove access that is no longer required and use a viewing role when someone only needs the results.',
          'Treat provisioning tokens separately from Dashboard roles. A User’s access to a page does not make it appropriate to copy a Site Group or Device token into email, chat, tickets, or shared documents.'
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Profile', to: '/profile' },
      { label: 'Open Site Groups', to: '/sitegroups' }
    ],
    related: ['profile-and-site-groups', 'sites-assets-and-devices'],
    nextArticle: {
      slug: 'understanding-device-status',
      description: 'Next, learn how Device status, recent activity, and available actions describe a camera-based deployment.'
    },
    legacy: false
  },
  {
    slug: 'mobile-data-in-the-dashboard',
    title: 'Working with Mobile Data',
    summary: 'Explore Site-level audience measurements that do not depend on a camera or Device installation.',
    category: 'basics',
    sources: [{ file: 'summary.pdf' }, { file: 'charts-and-insights.pdf' }],
    sections: [
      {
        id: 'what-mobile-data-is',
        title: 'Understand where Mobile Data fits',
        paragraphs: [
          'Mobile Data adds location-level audience measurements to a Site without requiring an AdMobilize Device or camera at that location. It can be the only source used by a Site or provide additional context in a supported view that also uses camera-based data.',
          'Because Mobile Data belongs to Sites, its filters and comparisons are Site-based. This differs from camera-based views, where Devices contribute measurements through Assets.'
        ]
      },
      {
        id: 'choose-sites',
        title: 'Select the Sites you want to study',
        paragraphs: [
          'Choose a Dashboard view that supports Mobile Data, then select the Sites that match your question. Use one Site when you are investigating a location in detail and several Sites when you want to compare a market, format, or portfolio.',
          'If a Site is not available, confirm that you selected the correct Site Group and that Mobile Data is connected to that Site. A camera-based Device at the same location does not automatically make the Site available in a Mobile Data view.'
        ]
      },
      {
        id: 'read-mobile-metrics',
        title: 'Read the available Mobile Data metrics',
        paragraphs: [
          'The metrics available to your account depend on its configured Dashboard view and data. Common examples include impressions, reach, frequency, signal volume, and match rate. Read the information supplied with a metric before using it in a customer report, especially when you are comparing it with a camera-based measurement that uses a different methodology.'
        ],
        tables: [
          {
            caption: 'Ways to explore Mobile Data',
            headers: ['Area', 'Useful for'],
            rows: [
              ['Summary', 'Reviewing the main Site-level KPIs and recent change.'],
              ['Charts', 'Seeing how an available metric changes over time.'],
              ['Insights', 'Comparing the selected Sites in a table.'],
              ['Discovery', 'Exploring available Site metrics on a map and in a location list.']
            ]
          }
        ],
        figures: [
          {
            src: 'assets/documentation/examples/analytics-mobile-impressions-reach-and-visitor-profiles.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Mobile Charts showing impressions, reach, frequency, and visitor interests',
            caption: 'Read Impressions and Reach separately, then use visitor profiles to explore the selected Sites.'
          }
        ]
      },
      {
        id: 'compare-carefully',
        title: 'Compare locations on equal terms',
        paragraphs: [
          'Use the same date range, timezone, and Site selection when you compare views. Differences in geographic coverage, operating periods, campaign timing, or available signals can affect the result, so record the scope alongside any exported number.',
          'When a Mobile Data result is unavailable or unexpected, check the Site association and filters before looking for a Device problem. The Site-level source does not depend on Device status.'
        ]
      },
      {
        id: 'visitor-profiles',
        title: 'Explore visitor profiles',
        paragraphs: [
          'Scroll below the Mobile KPIs to review age, generation, household income, interests, and supported interactions. Keep the Site selection and period consistent when moving between these charts. The following example shows the view available to a Brand user.'
        ],
        steps: [
          'Open Analytics → Charts and choose Mobile.',
          'Review the profile distributions for the selected Sites or campaign.',
          'Hover over a category to inspect its value and use the metric information control for its definition.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-user-nina/mobile-user-interactions-and-purchase-intent.jpg',
            frame: { width: 2127, height: 1068, sourceWidth: 2362 },
            alt: 'Brand-user Mobile interaction heatmap and purchase-intent profiles',
            caption: 'Nina’s campaign view shows populated interaction and purchase-intent profiles.'
          },
          {
            src: 'assets/documentation/examples/brand-user-nina/mobile-age-and-generation.jpg',
            frame: { width: 2127, height: 1068, sourceWidth: 2362 },
            alt: 'Brand-user Mobile age and generation distributions',
            caption: 'Age and generation show aggregate profiles for the selected campaign period.'
          }
        ]
      },
      {
        id: 'discovery',
        title: 'Find locations in Discovery',
        paragraphs: [
          'When Discovery is available, use its map and location list to compare Sites. Targeting filters help you inspect which locations match an audience characteristic.'
        ],
        steps: [
          'Select Mobile and open Analytics → Discovery.',
          'Review the Sites on the map and their signal volume in the location list.',
          'Choose a target characteristic, such as household income, and compare the resulting match rates.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/discovery-mobile-sites-map-and-signal-volume.jpg',
            frame: { width: 2128, height: 1008, sourceWidth: 2362 },
            alt: 'Mobile Discovery map and Site list with signal volumes',
            caption: 'Discovery connects Site-level measurements with their locations.'
          },
          {
            src: 'assets/documentation/examples/discovery-targeted-site-match-rates.jpg',
            frame: { width: 2128, height: 1008, sourceWidth: 2362 },
            alt: 'Discovery results with household-income targeting and Site match rates',
            caption: 'Apply an audience target before comparing the Site match rates.'
          }
        ]
      },
      {
        id: 'closed-funnel',
        title: 'Configure a Closed Funnel when available',
        paragraphs: [
          'Some Mobile templates include a Closed Funnel widget. It is not included in every account or Brand template. Use it only when the widget and the required interaction data are available.'
        ],
        steps: [
          'Find Closed Funnel on the Mobile Charts page.',
          'Use the step builder to choose the interaction stages you want to compare.',
          'Review the selected stages before interpreting the funnel result.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/analytics-mobile-closed-funnel-step-builder.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Closed Funnel step builder with selectable Mobile interaction stages',
            caption: 'Configure the stages supported by your account before reading the funnel.'
          }
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Summary', to: '/summary' },
      { label: 'Open Charts', to: '/charts' },
      { label: 'Open Insights', to: '/insights' }
    ],
    related: ['solutions-and-data-sources', 'sites-assets-and-devices', 'why-data-may-be-missing-or-delayed'],
    nextArticle: {
      slug: 'exports-and-reports',
      description: 'Next, choose how to export or deliver the analytics your team is ready to share.'
    },
    legacy: false
  },
  {
    slug: 'understanding-device-status',
    title: 'Understanding Device Status',
    summary: 'Use Device status, last detection, and available actions to understand the operational state of a camera-based deployment.',
    category: 'basics',
    sources: [{ file: 'management.pdf' }, { file: 'notifications.pdf' }],
    sections: [
      {
        id: 'status-and-data',
        title: 'Separate Device health from analytics',
        paragraphs: [
          'Device status tells you whether the connected hardware and application are available for remote work. It does not, by itself, prove that the camera is positioned correctly or that a particular metric is being produced. Review status together with the last detection time, solution, camera preview when authorized, and the analytics period you are investigating.',
          'A Device can be Online now even if the period you selected contains no data, and an Offline Device can still have historical data from an earlier period. Always match the operational check to the time covered by the question.'
        ]
      },
      {
        id: 'status-meanings',
        title: 'Read the status shown in the Device list',
        tables: [
          {
            caption: 'Common Device statuses',
            headers: ['Status', 'What it generally indicates'],
            rows: [
              ['Online', 'The Device is communicating and its application is available.'],
              ['Offline', 'The Device is not currently communicating with the platform.'],
              ['Stopped', 'The application has been stopped.'],
              ['No Camera', 'The Device is communicating, but the configured camera source is unavailable.'],
              ['Updating', 'An application or configuration update is in progress.'],
              ['Archived', 'The Device has been archived and is separated from active Devices in management views.']
            ]
          }
        ],
        paragraphs: [
          'Statuses can change while the Device reports new information. Refresh the list after an update or remote action before deciding that the action failed.'
        ]
      },
      {
        id: 'available-actions',
        title: 'Use only the actions available for the current status',
        paragraphs: [
          'The Dashboard enables remote actions according to the Device’s current state. Preview is intended for checking the camera view, while start, stop, and restart change the application state. Allow an action time to complete and review the resulting status before sending another command.'
        ],
        tables: [
          {
            caption: 'Action availability in the standard Device view',
            headers: ['Action', 'Available status'],
            rows: [
              ['Preview', 'Online'],
              ['Restart', 'Online or No Camera'],
              ['Stop', 'Online or No Camera'],
              ['Start', 'Stopped']
            ]
          }
        ]
      },
      {
        id: 'investigate-a-status',
        title: 'Investigate before escalating',
        steps: [
          'Confirm the affected Device, Site Group, Site, and Asset.',
          'Review its current status, last detection, application version, and recent notifications.',
          'Check the camera source or request an authorized preview when the status is No Camera.',
          'Use an available remote action only when it matches your operating procedure.',
          'If the issue continues, record the Device ID, timestamps, timezone, and actions already attempted before contacting support.'
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Devices', to: '/devices' },
      { label: 'Open Notifications', to: '/notifications' }
    ],
    related: ['device-notifications', 'workspace-management', 'why-data-may-be-missing-or-delayed'],
    nextArticle: {
      slug: 'why-data-may-be-missing-or-delayed',
      description: 'Next, work through the scope, source, timing, and Device checks behind an unexpected result.'
    },
    legacy: false
  },
  {
    slug: 'why-data-may-be-missing-or-delayed',
    title: 'Why Data May Be Missing or Delayed',
    summary: 'Work through the most common scope, time, access, and source checks before escalating an unexpected analytics result.',
    category: 'basics',
    sources: [{ file: 'main-filters.pdf' }, { file: 'management.pdf' }, { file: 'notifications.pdf' }],
    sections: [
      {
        id: 'begin-with-scope',
        title: 'Begin with the selected scope',
        paragraphs: [
          'An empty chart often starts with a mismatch between the selected view and the data available to it. Confirm the Site Group, Dashboard view, Sites or Devices, date range, and timezone before changing the deployment. Each of those controls can remove otherwise valid data from the result.',
          'If only one location is missing, compare its associations with a location that appears correctly. Check that the Site belongs to the selected Site Group, the Asset and Device relationships are correct for camera-based data, and the Site has the required Site-level source for Mobile Data.'
        ]
      },
      {
        id: 'match-source-and-time',
        title: 'Match the data source to the selected time',
        paragraphs: [
          'Choose a period when the source was expected to produce data. A Device that came online today cannot supply camera measurements for last month, while an Offline Device may still have historical results from before it lost connectivity. Mobile Data does not depend on Device status, so investigate its Site association and availability instead.',
          'Check the Site Group timezone and any time-of-day or weekday filters. A narrow operating window, an unexpected timezone, or a period that crosses midnight can make valid activity appear outside the selected range.'
        ]
      },
      {
        id: 'use-the-symptom',
        title: 'Let the symptom guide the next check',
        tables: [
          {
            caption: 'Common symptoms and first checks',
            headers: ['Symptom', 'Check first'],
            rows: [
              ['The entire view is empty', 'Site Group, Dashboard view, date range, source compatibility, and active filters.'],
              ['One Site or Device is missing', 'Access, Site membership, Asset association, and source availability.'],
              ['Only recent data is missing', 'Current source status, timezone, recent notifications, and whether processing is still underway.'],
              ['The result is lower than expected', 'Selected sources, operating hours, camera field of view, and comparison period.'],
              ['The comparison looks unusual', 'Previous-period coverage, holidays, closures, campaigns, and filter changes.']
            ]
          }
        ]
      },
      {
        id: 'prepare-for-support',
        title: 'Prepare useful context for support',
        paragraphs: [
          'If the checks do not explain the result, capture enough context for someone else to reproduce it. Include the Site Group, selected Dashboard view, affected Sites or Devices, date range, interval, timezone, expected result, and what you observed. Add the Device status and last detection when the issue involves camera-based data.',
          'Do not include passwords or provisioning tokens. A clear scope and timestamp are more useful for investigation than credentials and can be shared without exposing account secrets.'
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Summary', to: '/summary' },
      { label: 'Open Devices', to: '/devices' },
      { label: 'Open Notifications', to: '/notifications' }
    ],
    related: ['filters-and-date-ranges', 'understanding-device-status', 'mobile-data-in-the-dashboard'],
    nextArticle: {
      slug: 'dashboard-glossary',
      description: 'Next, use the glossary to review the organization, analytics, and operational terms used across the Dashboard.'
    },
    legacy: false
  },
  {
    slug: 'analytics-and-insights',
    title: 'Analytics and Insights',
    summary: 'Move from a high-level result to the charts, comparisons, and Device-level detail that help explain what changed.',
    category: 'basics',
    sources: [{ file: 'charts-and-insights.pdf' }, { file: 'summary.pdf' }],
    sections: [
      {
        id: 'charts',
        title: 'Read Charts',
        paragraphs: [
          'Charts is where a headline number becomes something you can investigate. Each widget reflects the selected solution, Site Group, Devices, and date range, so begin by checking the filters and date range above the page. Then use the information control on a chart to confirm what its values represent before comparing it with another metric or period.'
        ],
        paragraphLinks: [{
          label: 'filters and date range',
          slug: 'filters-and-date-ranges',
          section: 'define-the-scope'
        }],
        figures: [
          {
            src: 'assets/documentation/examples/analytics-audience-kpis-and-demographics.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Audience Charts with populated KPIs, demographics, and impression trends',
            caption: 'Charts expands the selected scope into totals, time series, and supporting breakdowns.'
          }
        ]
      },
      {
        id: 'widget-customization',
        title: 'Customize widgets',
        paragraphs: [
          'A useful Charts page does not need to show every available metric. Select Customize Widgets and keep the charts that support the question you are working on. This selection also determines which widgets appear in generated PDF reports, so review it before sharing a report outside the Dashboard.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/analytics-customize-widgets-dialog.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Customize Widgets dialog listing selectable Audience widgets',
            caption: 'The selected widgets determine the content of the Charts page and its Quick PDF output.'
          }
        ],
        steps: [
          'Open Analytics → Charts and select Customize Widgets.',
          'Select the widgets you want to include and clear those you do not need.',
          'Select Save, then review the page before exporting it.'
        ]
      },
      {
        id: 'insights',
        title: 'Compare Devices in Insights',
        paragraphs: [
          'Once a chart shows when a change happened, Insights can help you find where it came from. The page groups results by Device, with one row for each Device in the selected Site Group and scope. Columns and sorting options change with the selected solution because Audience and Traffic deployments answer different questions.',
          'Growth compares the selected period with the immediately preceding period of the same length. For example, a five-day range is compared with the previous five days. Keep that comparison window in mind when a label looks surprising, especially after changing the date range.'
        ],
        steps: [
          'Open Analytics → Insights using the same solution and date range as Charts.',
          'Compare the Device rows and sort by the metric you are investigating.',
          'Check each row’s totals and growth before attributing a change to a location.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/insights-audience-device-comparison.jpg',
            frame: { width: 2117, height: 1008, sourceWidth: 2362 },
            alt: 'Audience Insights table comparing impressions, views, and growth across Devices',
            caption: 'Compare source Devices to identify where the change occurred.'
          }
        ]
      },
      {
        id: 'growth',
        title: 'Interpret growth',
        paragraphs: [
          'Growth labels provide a quick way to scan the table, but the percentage change remains the more precise value. The current Dashboard assigns each label using the boundaries below.'
        ],
        tables: [
          {
            caption: 'Growth labels',
            headers: ['Label', 'Percentage change'],
            rows: [
              ['Falling', 'Less than -10%'],
              ['Decreasing', '-10% through values less than -5%'],
              ['Neutral', '-5% through 5%'],
              ['Rising', 'Greater than 5% through 10%'],
              ['Very Good', 'Greater than 10%']
            ]
          }
        ]
      },
      {
        id: 'time-patterns',
        title: 'Review hourly and daily patterns',
        paragraphs: [
          'Hourly and daily charts keep the selected data but regroup it by hour of day or day of week. They are useful for recurring patterns, such as a busy lunch period or a stronger weekday. Hover over a point or category to inspect the value behind the pattern.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/analytics-audience-hourly-and-weekday-patterns.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Audience hourly and weekday charts',
            caption: 'Hour-of-day and weekday charts help identify recurring busy periods.'
          }
        ]
      },
      {
        id: 'solution-insights',
        title: 'Use solution-specific Insights',
        paragraphs: [
          'Insights shows only the fields supported by the selected solution and the data available from the current Devices. For definitions and interpretation guidance, continue with Audience metrics or Traffic metrics after you identify the result you want to understand.'
        ],
        paragraphLinks: [
          { label: 'Audience metrics', slug: 'audience-metrics', section: 'core-metrics' },
          { label: 'Traffic metrics', slug: 'traffic-metrics', section: 'people-and-vehicle-counts' }
        ],
        tables: [
          {
            caption: 'Examples of solution-specific Insights',
            headers: ['Solution', 'Available detail'],
            rows: [
              [
                'Audience',
                'Impressions, views, age distribution, gender distribution, and growth. Hover details can expose the complete distribution behind a displayed summary.'
              ],
              ['Traffic', 'People and vehicle counts, average speed, supported classifications, and growth, when available.']
            ]
          }
        ]
      },
      {
        id: 'summary-context',
        title: 'Move from Summary to detail',
        paragraphs: [
          'Summary is useful for noticing that something changed. Open Charts when you want to see the shape of that change, then use Insights to compare the Devices behind it. When the explanation is ready to share, Exports and Reports covers both quick chart downloads and scheduled delivery.'
        ],
        paragraphLinks: [{
          label: 'Exports and Reports',
          slug: 'exports-and-reports',
          section: 'report-types'
        }]
      }
    ],
    dashboardLinks: [
      { label: 'Open Charts', to: '/charts' },
      { label: 'Open Insights', to: '/insights' }
    ],
    related: ['introduction', 'exports-and-reports'],
    nextArticle: {
      slug: 'mobile-data-in-the-dashboard',
      description: 'Next, explore the Site-level audience measurements available through Mobile Data.'
    },
    legacy: false
  },
  {
    slug: 'exports-and-reports',
    title: 'Exports and Reports',
    summary: 'Choose the right way to take analytics out of the Dashboard, from a quick chart download to a recurring report for your team.',
    category: 'basics',
    sources: [{ file: 'export-data.pdf' }, { file: 'reports.pdf' }],
    sections: [
      {
        id: 'chart-csv',
        title: 'Export one chart to CSV',
        paragraphs: [
          'When someone needs the data behind a single chart, a CSV export is usually the shortest path. Open the chart’s three-dot menu and select Export to CSV. The downloaded file represents that widget, using the scope and period currently applied on Charts, so check the filters before you export.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/analytics-widget-export-csv-menu.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Audience widget menu with Export to CSV action',
            caption: 'Export to CSV downloads the data behind this widget using its current scope.'
          }
        ]
      },
      {
        id: 'report-types',
        title: 'Generate an analytics report',
        paragraphs: [
          'Use Generate Report from Charts when a single widget does not carry enough context or when the result needs to be delivered repeatedly. Before submitting the report, review the selected Site Group, Devices, dates, and timezone. Working with Filters and Date Ranges explains how those choices shape the result.'
        ],
        paragraphLinks: [{
          label: 'Working with Filters and Date Ranges',
          slug: 'filters-and-date-ranges',
          section: 'define-the-scope'
        }],
        tables: [
          {
            caption: 'Report types',
            headers: ['Type', 'Behavior'],
            rows: [
              ['Quick CSV', 'Uses the timezone, date range, and Devices currently selected on Charts.'],
              ['Custom CSV', 'Lets you configure time parameters, delivery frequency, grouping, and data columns.'],
              [
                'Quick PDF',
                'Opens the browser print dialog for the Charts page; choose Save as PDF to create a file.'
              ]
            ]
          }
        ],
        steps: [
          'Open Charts and confirm the filters and selected widgets.',
          'Select Generate Report and choose one of the formats available to your account.',
          'For Quick CSV, review the selected sources and period, enter the recipient, and select Send Report.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/reports-choose-csv-or-pdf.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Generate Report menu with Quick CSV, Custom CSV, and Quick PDF',
            caption: 'Available formats depend on the account and Dashboard view.'
          }
        ]
      },
      {
        id: 'quick-pdf',
        title: 'Save the Charts page as PDF',
        paragraphs: [
          'Quick PDF uses the current Charts page and opens the browser print dialog. The following example shows a Brand user’s menu, where Quick PDF is the available report format.'
        ],
        steps: [
          'Choose the campaign or sources, date range, and widgets to include.',
          'Select Generate Report → Quick PDF.',
          'In the browser print dialog, choose Save as PDF as the destination, review the preview, and save the file.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/brand-user-nina/reports-quick-pdf-menu.jpg',
            alt: 'Brand-user Generate Report menu offering Quick PDF',
            caption: 'Select Quick PDF to open the browser print dialog.'
          }
        ]
      },
      {
        id: 'custom-csv',
        title: 'Configure a Custom CSV',
        paragraphs: [
          'A Custom CSV is useful when the recipient needs a repeatable dataset instead of the visual Dashboard experience. You can generate it once or place it on a recurring schedule. Set the time parameters first, then choose only the columns the recipient needs; a smaller, clearly scoped file is easier to interpret and maintain.'
        ],
        tables: [
          {
            caption: 'Custom CSV parameters',
            headers: ['Parameter', 'Options'],
            rows: [
              ['Range', 'Selects the reporting period.'],
              ['Interval', 'Groups rows by Minute, Hour, Day, Week, Week (Starting on Monday), Month, or Year.'],
              [
                'Frequency',
                'Single, Daily, Weekly, or Monthly. Weekly and Monthly expose the applicable delivery-day control.'
              ],
              ['Timezone', 'Converts report timestamps to the selected timezone.'],
              ['Grouping', 'For Audience reports, groups by detection or by Device when those formats are available.'],
              ['Select Data', 'Chooses the columns included in the CSV.']
            ]
          }
        ],
        steps: [
          'Select Generate Report → Custom CSV.',
          'Set the range, interval, timezone, and grouping. Choose Single for a one-time delivery or configure a recurring frequency.',
          'Select Next to choose the columns.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/reports-custom-csv-step-01-time-parameters.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Custom CSV time parameters with range, interval, frequency, and timezone',
            caption: 'Step 1: define the period and time parameters before selecting data.'
          }
        ]
      },
      {
        id: 'select-csv-data',
        title: 'Choose the CSV columns',
        steps: [
          'In Select Data, select the identification fields and metrics the recipient needs.',
          'Select Generate to open the final review.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/reports-custom-csv-step-02-select-data.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'Custom CSV Select Data step with available columns',
            caption: 'Step 2: choose the fields included in the exported CSV.'
          }
        ]
      },
      {
        id: 'review-csv-delivery',
        title: 'Review and send the CSV',
        steps: [
          'Review the report summary, including the period, timezone, and source Devices.',
          'Enter the recipient email address.',
          'Select Send Report, or Send Report Recurrently for a recurring report.'
        ],
        figures: [
          {
            src: 'assets/documentation/examples/reports-quick-csv-review-range-devices-and-email.jpg',
            frame: { width: 2127, height: 1008, sourceWidth: 2362 },
            alt: 'CSV report review dialog showing period, Device selections, and recipient email',
            caption: 'The final delivery dialog lets you check the scope and recipient before sending.'
          }
        ]
      },
      {
        id: 'manage',
        title: 'Manage recurring reports',
        paragraphs: [
          'The Reports page is the working list for everything your team has configured. Each row shows the report name, recipient, Site Group, delivery frequency, status, and most recent delivery. When available, it also shows when the generated file expires, which is useful when someone returns to an older delivery.'
        ],
        bullets: [
          'Pause an active recurring report to stop delivery; resume it when delivery should continue.',
          'Delete a report when it is no longer needed.',
          'Open a report to update supported details such as its name, recipient, frequency, grouping, and timezone.',
          'Devices, advertisers, and media are displayed in their detail tabs. Create a new report when those selections must change.'
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Charts', to: '/charts' },
      { label: 'Open Reports', to: '/reports' }
    ],
    related: ['analytics-and-insights', 'data-api'],
    nextArticle: {
      slug: 'profile-and-site-groups',
      description: 'Next, review the account and Site Group settings that shape access and analytics presentation.'
    },
    legacy: false
  },
  {
    slug: 'dashboard-glossary',
    title: 'Dashboard Glossary',
    summary: 'Find plain-language definitions for the organization, analytics, and operational terms used throughout the Dashboard.',
    category: 'basics',
    sources: [{ file: 'summary.pdf' }, { file: 'management.pdf' }, { file: 'projects.pdf' }],
    sections: [
      {
        id: 'organization-terms',
        title: 'Organization and deployment terms',
        paragraphs: [
          'These terms describe how the Dashboard connects an organization to its physical locations and measurement sources. Read them from top to bottom when you are learning the structure for the first time.'
        ],
        tables: [
          {
            caption: 'Organization and deployment glossary',
            headers: ['Term', 'Meaning'],
            rows: [
              ['Dashboard', 'The web service used to review measurements, manage a deployment, and create exports or reports.'],
              ['Site Group', 'A workspace and access boundary containing Sites, Users, preferences, and available data.'],
              ['Site', 'A physical location or installation. It can contain Assets and receive Site-level sources such as Mobile Data.'],
              ['Asset', 'A screen or another physical media item at a Site. It can be mapped to a CMS player and connected to Devices.'],
              ['Device', 'Connected hardware that receives camera input and runs an AdMobilize solution.'],
              ['User', 'A person with access to one or more Site Groups. Their role controls what they can view or manage.'],
              ['Provisioning token', 'A secret value used when connecting a Site Group or Device. It should not be shared in tickets, chat, or source control.']
            ]
          }
        ]
      },
      {
        id: 'analytics-terms',
        title: 'Analytics terms',
        paragraphs: [
          'Analytics terms describe the scope and shape of a result. The selected Dashboard view and available data determine which metrics appear for your account.'
        ],
        tables: [
          {
            caption: 'Analytics glossary',
            headers: ['Term', 'Meaning'],
            rows: [
              ['Solution', 'The kind of vision activity being measured: Audience or Traffic.'],
              ['Data source', 'The origin of a measurement, such as Audience, Traffic, or Mobile Data.'],
              ['Dashboard view', 'A configured collection of compatible KPIs, charts, filters, and data sources.'],
              ['Metric', 'A measured or calculated value presented in a KPI, chart, table, export, or report.'],
              ['Date range', 'The first and last dates included in an analytics request.'],
              ['Interval', 'The unit used to group the selected period, such as Hour, Day, Week, Month, or Year.'],
              ['Timezone', 'The regional time setting used to interpret and display timestamps.'],
              ['Summary', 'The high-level view of key metrics and changes for the selected scope.'],
              ['Charts', 'The area used to explore individual metrics over time and export chart data.'],
              ['Insights', 'The area used to compare results across Sites or Devices.'],
              ['Discovery', 'A Mobile Data area for exploring available Site metrics geographically and in a list.']
            ]
          }
        ]
      },
      {
        id: 'data-and-integration-terms',
        title: 'Data and integration terms',
        paragraphs: [
          'These terms describe how measurements reach the Dashboard and how outside systems can add context or receive results.'
        ],
        tables: [
          {
            caption: 'Data and integration glossary',
            headers: ['Term', 'Meaning'],
            rows: [
              ['Mobile Data', 'Site-level audience data that does not require a camera or AdMobilize Device at the Site.'],
              ['CMS', 'A content management system used to schedule, manage, or record media playback.'],
              ['Proof of play', 'A record showing when particular media played on a screen or player.'],
              ['Integration', 'A connection that exchanges information between AdMobilize and another system.'],
              ['Report', 'A generated analytics file that can be created once or delivered on a recurring schedule.'],
              ['Export', 'A downloaded representation of selected Dashboard data, such as a chart CSV.'],
              ['Edge processing', 'Processing camera input on the local Device before anonymous, aggregate measurements are sent to the platform.']
            ]
          }
        ]
      }
    ],
    dashboardLinks: [
      { label: 'Open Summary', to: '/summary' },
      { label: 'Open Sites', to: '/sites' },
      { label: 'Open Devices', to: '/devices' }
    ],
    related: ['introduction', 'sites-assets-and-devices', 'solutions-and-data-sources'],
    nextArticle: {
      slug: 'audience-metrics',
      description: 'Next, continue into the Analytics guides with a closer look at Audience metrics.'
    },
    legacy: false
  }
];
